import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { useHistory } from "react-router-dom";
import {
  useGetFilingDetailsByStateIdQuery,
  useGetFilingDetailsQuery,
} from "store/apis/filing";
import { useStartFilingSubscription } from "pages/SalesTax/Filings/useStartFilingSubscription";
import { skipToken } from "@reduxjs/toolkit/query";

type NudgeAddFilingToCartModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  groupId: string;
  stateId: string;
  stateName: string;
};

export const NudgeAddFilingToCartModal = ({
  isOpen,
  onClose,
  entityId,
  groupId,
  stateId,
  stateName,
}: NudgeAddFilingToCartModalProps) => {
  const { data: actionRequiredFiling, isFetching: isFetchingActionRequired } =
    useGetFilingDetailsByStateIdQuery(
      isOpen && entityId && stateId ? { entityId, stateId } : skipToken
    );

  const { data: filing, isFetching: isFetchingFiling } =
    useGetFilingDetailsQuery(
      actionRequiredFiling?.uuid
        ? { entityId, fillingId: actionRequiredFiling.uuid }
        : skipToken
    );

  const isResolvingFiling = isFetchingActionRequired || isFetchingFiling;

  const { isAddingToCart, startSubscriptionCheckout } =
    useStartFilingSubscription(
      filing?.payment_schedule ?? "",
      filing?.state ?? stateName,
      filing
        ? {
            filingId: filing.uuid,
            startDate: filing.filing_period_start_date,
            endDate: filing.filing_period_end_date,
          }
        : undefined
    );

  const history = useHistory();

  const filingsLink = `/sales-tax/state-filings?entity=${entityId}&company=${groupId}&search=${encodeURIComponent(
    stateName
  )}`;

  const handleAddFilingToCart = async () => {
    await startSubscriptionCheckout();
    onClose();
  };

  const handleGoToFilings = () => {
    onClose();
    history.push(filingsLink);
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="small">
        <StatusModal type="success" title={`${stateName} is now registered`}>
          Add its sales tax filing to your cart so it doesn&apos;t get missed.
        </StatusModal>
        <Modal.Footer className="t-flex t-justify-end t-gap-3">
          <Button onClick={onClose} type="button">
            Not now
          </Button>
          {filing || isResolvingFiling ? (
            <Button
              customType="primary"
              type="button"
              isLoading={isResolvingFiling || isAddingToCart}
              disabled={isResolvingFiling || isAddingToCart}
              onClick={() => void handleAddFilingToCart()}
            >
              Add filing to cart
            </Button>
          ) : (
            <Button
              customType="primary"
              type="button"
              onClick={handleGoToFilings}
            >
              Go to filings
            </Button>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
